import { Box, Drawer, Stack } from "@mui/material";
import React, { useCallback } from "react";
import { BreadScrumHeader, HighlightSelectBox, SelectBox, SimpleSideNavBar } from "../";
import { LayoutProps } from "../../interfaces";
import { useLocation, useNavigate } from "react-router-dom";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { DragHandle } from "@mui/icons-material";
import { LocalStorageKeys } from "../../utils";

const ResizableSideBar: React.FC<Partial<LayoutProps> & { openWebNav: boolean }> = ({ mainPanel, locationState, shouldAddStyle, hideSideNavBar, navBars = [], avoidNavigate, selected, openWebNav, navBarTitle, showNavbarSearch, onClick }) => {
    return (
        <PanelGroup autoSaveId={LocalStorageKeys.resizeSideNav} direction="horizontal">
            {!hideSideNavBar && navBars.length > 0 && openWebNav && (
                <>
                    <Panel defaultSize={20}>
                        <SimpleSideNavBar
                            navBars={navBars}
                            variant={"whitecard"}
                            title={navBarTitle}
                            showSearch={showNavbarSearch}
                            avoidNavigate={avoidNavigate}
                            onClick={onClick}
                            selected={selected}
                            shouldAddStyle={shouldAddStyle}
                            locationState={locationState}
                        />
                    </Panel>
                    <PanelResizeHandle
                        style={{
                            position: "relative"
                        }}
                    >
                        <Box
                            component={"div"}
                            title="Resize Width"
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                cursor: "ew-resize",
                                position: "absolute",
                                right: 0,
                                width: '4px',
                                top: 0,
                                bottom: 0,
                            }}
                        >
                            <DragHandle sx={{ transform: "rotate(90deg)", cursor: "ew-resize", marginLeft: '-6px' }} fontSize="small" />
                        </Box>
                    </PanelResizeHandle>
                </>
            )}
            <Panel>
                {/* Main Panel */}
                <Stack
                    sx={{
                        height: "100%",
                        overflow: "auto",
                        pl: { xs: 0, sm: openWebNav && navBars.length > 0 ? 1 : 0 },
                    }}>
                    {mainPanel}
                </Stack>
            </Panel>
        </PanelGroup>
    );
};


const StaticComponent: React.FC<Partial<LayoutProps> & { openWebNav: boolean }> = ({ mainPanel, locationState, shouldAddStyle, hideSideNavBar, navBars = [], avoidNavigate, selected, openWebNav, navBarTitle, showNavbarSearch, onClick }) => {
    return (
        <>
            {!hideSideNavBar && navBars.length > 0 && openWebNav &&
                <Stack
                    sx={{
                        height: "100%",
                        width: { xs: 300, md: 350, lg: 400 },
                        display: { xs: "none", sm: "block" }
                    }}>
                    <SimpleSideNavBar
                        navBars={navBars}
                        variant={"whitecard"}
                        title={navBarTitle}
                        showSearch={showNavbarSearch}
                        avoidNavigate={avoidNavigate}
                        onClick={onClick}
                        selected={selected}
                        shouldAddStyle={shouldAddStyle}
                        locationState={locationState}
                    />
                </Stack>}

            {/* Main Panel */}
            <Stack
                sx={{
                    height: "100%",
                    overflow: "auto",
                    width: "100%",
                    pl: { xs: 0, sm: openWebNav && navBars.length > 0 ? 1 : 0 },
                }}>
                {mainPanel}
            </Stack>
        </>
    );
};

export const Layout: React.FC<LayoutProps> = ({
    history = [], currentPath = '', navBars = [], navBarTitle, mainPanel = <>Main Panel</>, otherBreadscrumProps = {}, hideNavBarByDefault = false, hideBreadScrum = false,
    showDropInsteadOfNavInMobile = false, navBarHasChild = false, showNavbarSearch = false, onClick = (nav) => false, selected = [], avoidNavigate = false, resizable = false,
    shouldAddStyle = true, hideSideNavBar = false,
    locationState, onBackBtnClick
}) => {

    const location = useLocation();
    const navigate = useNavigate();

    const [openWebNav, setOpenWebNavBar] = React.useState(!hideNavBarByDefault);
    const [openMobileNav, setOpenMobileNavBar] = React.useState(false);

    const toogleWebNav = () => { setOpenWebNavBar(!openWebNav) };
    const toogleMobileNav = () => { setOpenMobileNavBar(!openMobileNav) };

    const giveMeSelectDropDownValue = useCallback(() => {
        let selected = null;
        for (let index = 0; index < navBars.length; index++) {
            const nav = navBars[index];
            selected = nav?.subNav?.filter(_ => _.path === location?.pathname || _.path === location?.pathname + location?.search?.replaceAll('%20', ' '))?.map((_) => ({ value: `${_.id}:${nav.id}`, label: `${nav.title}: ${_.title}`, ..._ }))?.[0];
            if (selected) {
                break;
            }
        }
        return selected;
        // eslint-disable-next-line
    }, [location]);

    const giveMeDropDownValue = useCallback(() => {
        return navBars.filter(_ => _.path === location?.pathname || _.path === location?.pathname + location?.search?.replaceAll('%20', ' ')).map((_) => ({ value: _.id, label: _.title, ..._ }))[0]
        // eslint-disable-next-line
    }, [location])

    React.useEffect(() => {
        setOpenWebNavBar(!hideNavBarByDefault)
    }, [hideNavBarByDefault])

    return (
        <Box sx={{ height: "100%" }}>

            {/* Header */}
            {!hideBreadScrum && <BreadScrumHeader
                {...otherBreadscrumProps}
                openWebNav={openWebNav}
                toogleWebNav={toogleWebNav}
                toogleMobileNav={toogleMobileNav}
                history={history}
                currentPath={currentPath}
                onBackBtnClick={onBackBtnClick}
            />}

            {showDropInsteadOfNavInMobile && <Box sx={{ display: { xs: "block", sm: "none" }, paddingTop: 1, paddingRight: 1, paddingBottom: 2 }}>
                {!navBarHasChild && <HighlightSelectBox
                    label={navBarTitle ? navBarTitle : ""}
                    margin={"none"}
                    required={true}
                    value={giveMeDropDownValue()}
                    options={navBars?.map(_ => ({ value: _.id, label: _.title, ..._ }))}
                    onChange={(data: any) => {
                        if (data) {
                            navigate(data?.path);
                        }
                    }}
                />}
                {navBarHasChild && <SelectBox
                    label={navBarTitle ? navBarTitle : ""}
                    value={giveMeSelectDropDownValue()}
                    options={navBars?.map(_ => ({
                        value: _.id, label: _.title,
                        options: _?.subNav?.map(nav => ({ value: `${nav.id}:${_.id}`, label: nav.title, ...nav }))
                    }))}
                    onChange={(data: any) => {
                        if (data) {
                            navigate(data?.path);
                        }
                    }}
                />}
            </Box>}

            {/* Panel */}
            <Stack direction={"row"} sx={{ height: "calc(100% - 30px)", transition: 'all 1s', paddingTop: hideBreadScrum ? 0 : 1 }}>

                {/* Side Nav */}
                {resizable
                    ? <ResizableSideBar
                        mainPanel={mainPanel}
                        locationState={locationState}
                        shouldAddStyle={shouldAddStyle}
                        hideSideNavBar={hideSideNavBar}
                        navBars={navBars}
                        avoidNavigate={avoidNavigate}
                        selected={selected}
                        openWebNav={openWebNav}
                        navBarTitle={navBarTitle}
                        showNavbarSearch={showNavbarSearch}
                        onClick={onClick}
                    />
                    : <StaticComponent
                        mainPanel={mainPanel}
                        locationState={locationState}
                        shouldAddStyle={shouldAddStyle}
                        hideSideNavBar={hideSideNavBar}
                        navBars={navBars}
                        avoidNavigate={avoidNavigate}
                        selected={selected}
                        openWebNav={openWebNav}
                        navBarTitle={navBarTitle}
                        showNavbarSearch={showNavbarSearch}
                        onClick={onClick}
                    />
                }
            </Stack>

            {/* Mobile Drawer */}
            <Drawer anchor="left" open={openMobileNav} onClose={toogleMobileNav}>
                <SimpleSideNavBar
                    navBars={navBars}
                    avoidNavigate={avoidNavigate}
                    onClick={onClick}
                    selected={selected}
                    locationState={locationState}
                />
            </Drawer>
        </Box>
    );
}